import i18n from "../i18n";
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Scale, Plus, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend } from "recharts";

const FARM_TIMEZONE = "Asia/Manila";

function farmDateIso(date = new Date()) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: FARM_TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
}

export default function GrowthPage() {
    const [animals, setAnimals] = useState([]);
    const [species, setSpecies] = useState([]);
    const [selected, setSelected] = useState("");
    const [growth, setGrowth] = useState(null);
    const [dlg, setDlg] = useState(null);
    const [filterSpecies, setFilterSpecies] = useState("__all__");
    const [loadingPage, setLoadingPage] = useState(false);
    const [loadingGrowth, setLoadingGrowth] = useState(false);
    const [pageError, setPageError] = useState("");

    const load = async () => {
        setLoadingPage(true);
        setPageError("");
        try {
            const [a, s] = await Promise.all([
                api.get("/animals").then(r => r.data),
                api.get("/species").then(r => r.data),
            ]);
            setAnimals(a); setSpecies(s);
        } catch (err) {
            setPageError("Failed to load animals or species.");
            toast.error("Failed to load growth module data");
        } finally {
            setLoadingPage(false);
        }
    };
    useEffect(() => { load(); }, []);

    const loadGrowth = async (id) => {
        setSelected(id);
        if (!id) { setGrowth(null); return; }
        setLoadingGrowth(true);
        try {
            const r = await api.get(`/animals/${id}/growth`);
            setGrowth(r.data);
        } catch (err) {
            setGrowth(null);
            toast.error("Failed to load growth records for selected animal");
        } finally {
            setLoadingGrowth(false);
        }
    };

    const addWeight = async () => {
        if (!dlg.animal_id) return;
        const weight = Number(dlg.weight_kg);
        const feed = Number(dlg.feed_kg || 0);
        if (!Number.isFinite(weight) || weight <= 0) {
            toast.error("Weight must be greater than 0");
            return;
        }
        if (!Number.isFinite(feed) || feed < 0) {
            toast.error("Feed must be 0 or greater");
            return;
        }
        try {
            await api.post("/weight-records", {
                animal_id: dlg.animal_id,
                weight_kg: weight,
                feed_kg: feed,
                date: dlg.date || farmDateIso(),
                notes: dlg.notes || "",
            });
            toast.success("Weight recorded");
            setDlg(null);
            if (selected === dlg.animal_id) loadGrowth(selected);
        } catch (err) {
            toast.error("Failed to save weight record");
        }
    };

    const filtered = animals.filter(a => a.status === "active" && (filterSpecies === "__all__" || a.species_id === filterSpecies));
    const speciesMap = Object.fromEntries(species.map(s => [s.id, s]));

    return (
        <div className="space-y-6" data-testid="growth-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="tiny-label">{i18n.t("batch2.b9b664eafeff")}</div>
                    <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch2.b7f1480271f7")}</h1>
                    <p className="text-muted-foreground mt-1">{i18n.t("batch2.3ca127a5a35d")}</p>
                </div>
                <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                    <DialogTrigger asChild>
                        <Button onClick={() => setDlg({ animal_id: selected, weight_kg: "", feed_kg: "", date: farmDateIso(), notes: "" })} disabled={!selected} className="h-11" data-testid="add-weight-btn">
                            <Plus className="w-4 h-4 mr-1" />{i18n.t("batch2.23101365c88a")}
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{i18n.t("batch2.4a9db9b99989")}</DialogTitle>
                            <DialogDescription>{i18n.t("batch2.2868023ccc42")}</DialogDescription>
                        </DialogHeader>
                        {dlg && <div className="space-y-3">
                            <div>
                                <Label className="tiny-label">{i18n.t("batch2.7389b3b752fa")}</Label>
                                <Select value={dlg.animal_id} onValueChange={v => setDlg({ ...dlg, animal_id: v })}>
                                    <SelectTrigger className="h-11 mt-1" data-testid="weight-animal-select"><SelectValue placeholder={i18n.t("batch2.9815c7072808")} /></SelectTrigger>
                                    <SelectContent>{filtered.map(a => <SelectItem key={a.id} value={a.id}>{a.tag_id} · {a.name || "—"}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="tiny-label">{i18n.t("batch2.d24983206285")}</Label><Input type="number" step="0.1" value={dlg.weight_kg} onChange={e => setDlg({ ...dlg, weight_kg: e.target.value })} className="h-11 mt-1 font-mono" data-testid="weight-kg-input" /></div>
                                <div><Label className="tiny-label">{i18n.t("batch2.d7d33a37498a")}</Label><Input type="number" step="0.1" value={dlg.feed_kg} onChange={e => setDlg({ ...dlg, feed_kg: e.target.value })} className="h-11 mt-1 font-mono" data-testid="feed-kg-input" /></div>
                            </div>
                            <div><Label className="tiny-label">{i18n.t("batch2.eb9a4bc1c0c1")}</Label><Input type="date" value={dlg.date} onChange={e => setDlg({ ...dlg, date: e.target.value })} className="h-11 mt-1" /></div>
                            <div><Label className="tiny-label">{i18n.t("batch2.70440046a3dc")}</Label><Input value={dlg.notes} onChange={e => setDlg({ ...dlg, notes: e.target.value })} placeholder={i18n.t("batch2.d03becf78c55")} className="h-11 mt-1" /></div>
                            <Button onClick={addWeight} disabled={!dlg.animal_id || !dlg.weight_kg} className="w-full h-11" data-testid="save-weight-btn">{i18n.t("batch2.3d0aa2fbc3ed")}</Button>
                        </div>}
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4">
                    {loadingPage ? <p className="text-sm text-muted-foreground mb-2">{i18n.t("batch2.bc08c8ca11eb")}</p> : null}
                    {pageError ? <p className="text-sm text-destructive mb-2">{pageError}</p> : null}
                    <Label className="tiny-label">{i18n.t("batch2.bb6b3d02e567")}</Label>
                    <Select value={filterSpecies} onValueChange={setFilterSpecies}>
                        <SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">{i18n.t("batch2.c500690deb24")}</SelectItem>
                            {species.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Label className="tiny-label mt-3 block">{i18n.t("batch2.179ac1a75869")}</Label>
                    <Select value={selected} onValueChange={loadGrowth}>
                        <SelectTrigger className="h-10 mt-1" data-testid="growth-animal-select"><SelectValue placeholder={i18n.t("batch2.099f73ce4905")} /></SelectTrigger>
                        <SelectContent>
                            {filtered.map(a => <SelectItem key={a.id} value={a.id}>{a.tag_id} · {speciesMap[a.species_id]?.name || ""}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    {!loadingPage && filtered.length === 0 ? <p className="text-sm text-muted-foreground mt-2">{i18n.t("batch2.4bf117c4c02d")}</p> : null}
                </Card>

                {growth && growth.animal && <>
                    <Card className="p-4 md:col-span-2">
                        <div className="tiny-label">{growth.animal.tag_id} · {speciesMap[growth.animal.species_id]?.name || ""}</div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                            <div>
                                <div className="tiny-label">{i18n.t("batch2.931cd0b708ee")}</div>
                                <div className="text-2xl font-bold font-mono">{growth.current_weight_kg?.toFixed(1) || "—"} <span className="text-sm text-muted-foreground">{i18n.t("batch2.1389845b02c0")}</span></div>
                            </div>
                            <div>
                                <div className="tiny-label">{i18n.t("batch2.5e5837db8186")}</div>
                                <div className="text-2xl font-bold font-mono text-primary">{growth.total_gain_kg?.toFixed(1) || 0} <span className="text-sm text-muted-foreground">{i18n.t("batch2.1389845b02c0")}</span></div>
                            </div>
                            <div>
                                <div className="tiny-label">{i18n.t("batch2.246d291dae87")}</div>
                                <div className="text-2xl font-bold font-mono">{growth.total_feed_kg?.toFixed(1) || 0} <span className="text-sm text-muted-foreground">{i18n.t("batch2.1389845b02c0")}</span></div>
                            </div>
                            <div>
                                <div className="tiny-label">{i18n.t("batch2.52526afc1060")}</div>
                                <div className={`text-2xl font-bold font-mono ${growth.overall_fcr && growth.overall_fcr < 3 ? "text-primary" : growth.overall_fcr && growth.overall_fcr > 4 ? "text-destructive" : "text-amber-600"}`}>
                                    {growth.overall_fcr?.toFixed(2) || "—"}
                                </div>
                            </div>
                        </div>
                    </Card>
                </>}
            </div>

            {loadingGrowth ? (
                <Card className="p-6 text-center text-muted-foreground">{i18n.t("batch2.237a3b253245")}</Card>
            ) : null}

            {growth && growth.trend?.length > 0 && (
                <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />{i18n.t("batch2.382bc33f5991")}</h3>
                    <div style={{ minHeight: 280 }}>
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={growth.trend}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="date" fontSize={11} />
                            <YAxis fontSize={11} label={{ value: "kg", angle: -90, position: "insideLeft", fontSize: 11 }} />
                            <RTooltip />
                            <Legend />
                            <Line type="monotone" dataKey="weight_kg" name="Weight (kg)" stroke="#40916c" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="feed_kg" name="Feed (kg)" stroke="#e07a5f" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                    </ResponsiveContainer>
                    </div>
                    <Table className="mt-4">
                        <TableHeader><TableRow>
                            <TableHead>{i18n.t("batch2.eb9a4bc1c0c1")}</TableHead><TableHead className="text-right">{i18n.t("batch2.d24983206285")}</TableHead>
                            <TableHead className="text-right">{i18n.t("batch2.4cbdfa3a77db")}</TableHead><TableHead className="text-right">{i18n.t("batch2.b25db9c91647")}</TableHead>
                            <TableHead className="text-right">{i18n.t("batch2.1f85f16afefa")}</TableHead><TableHead>{i18n.t("batch2.70440046a3dc")}</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                            {growth.trend.map((t, i) => (
                                <TableRow key={`${t.date}-${i}`}>
                                    <TableCell className="font-mono text-xs">{t.date}</TableCell>
                                    <TableCell className="text-right font-mono font-semibold">{t.weight_kg?.toFixed(1)}</TableCell>
                                    <TableCell className="text-right font-mono">{t.feed_kg?.toFixed(1) || "—"}</TableCell>
                                    <TableCell className="text-right font-mono">{t.adg?.toFixed(3) || "—"}</TableCell>
                                    <TableCell className={`text-right font-mono ${t.fcr && t.fcr < 3 ? "text-primary" : t.fcr && t.fcr > 4 ? "text-destructive" : ""}`}>{t.fcr?.toFixed(2) || "—"}</TableCell>
                                    <TableCell className="text-xs">{t.notes}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {growth && growth.trend?.length === 0 && (
                <Card className="p-8 text-center">
                    <Scale className="w-12 h-12 mx-auto text-muted-foreground opacity-40 mb-3" />
                    <p className="text-muted-foreground">{i18n.t("batch2.70b6f53fbdf9")}</p>
                </Card>
            )}
        </div>
    );
}
